import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, usePermission } from "../contexts/AuthContext";
import api, { errorMessage, API_BASE } from "../lib/api";
import { fmtDateTime } from "../lib/tz";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import PhotoAttachmentsField from "../components/PhotoAttachmentsField";
import AttachmentViewer from "../components/AttachmentViewer";
import TourScanLogTable from "../components/TourScanLogTable";
import ResendReportEmailButton from "../components/ResendReportEmailButton";
import BulkResendBar from "../components/BulkResendBar";
import useBulkSelection from "../hooks/useBulkSelection";
import LinkedEntityFilters from "../components/LinkedEntityFilters";
import StickyFiltersBar, { useFullscreenRoute } from "../components/StickyFiltersBar";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";
import { Plus, FileSignature, Trash2, Image as ImageIcon, Pencil } from "lucide-react";
import { AdminCommentsPanel } from "../components/AdminCommentsPanel";
import ApprovalActions, { ApprovalPill } from "../components/ApprovalActions";
import { DAR_QUICK_PHRASES as DAR_QUICK_PHRASES_DEFAULT, appendPhrase, extractPlaceholders } from "../lib/darQuickPhrases";
import DarPhraseFillModal from "../components/DarPhraseFillModal";
import { toast } from "sonner";

const EQUIPMENT_OPTIONS = [
  "Fire extinguishers",
  "Emergency exits",
  "Lighting",
  "Surveillance cameras",
  "Access points",
  "Perimeter fencing",
  "Alarms",
  "Fire doors",
];

const empty = {
  shift_id: "",
  narrative: "",
  visitor_count: 0,
  vehicle_count: 0,
  equipment_inspected: [],
  maintenance_issues: "",
  notable_events: [],
  weather_conditions: "",
  handover_notes: "",
  photo_attachments: [],
};

export default function DAR() {
  const { user } = useAuth();
  const canCreateDar = usePermission("dar:create");
  const canDeleteDar = usePermission("dar:delete");
  const [items, setItems] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [filters, setFilters] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [activeShiftInfo, setActiveShiftInfo] = useState(null); // { shift, site } | null
  const [siteFilter, setSiteFilter] = useState(""); // narrows shift options in supervisor mode
  const [quickPhrases, setQuickPhrases] = useState(DAR_QUICK_PHRASES_DEFAULT);
  const [phraseTemplate, setPhraseTemplate] = useState(null); // when set, opens fill-modal
  const bulk = useBulkSelection();
  const location = useLocation();
  const nav = useNavigate();
  const isFs = useFullscreenRoute(location.pathname);
  useRememberWindowGeometry(isFs ? location.pathname : null);

  useEffect(() => {
    // Pull tenant-configured phrase presets — fall back to hardcoded defaults
    // if the tenant admin hasn't customized them.
    api.get("/agency/dar-quick-phrases")
      .then((r) => {
        const list = r.data?.phrases;
        if (Array.isArray(list) && list.length) setQuickPhrases(list);
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const load = useCallback(async () => {
    const [a, b, s] = await Promise.all([
      api.get("/dar"),
      api.get("/shifts"),
      api.get("/sites").catch(() => ({ data: [] })),
    ]);
    setItems(a.data || []);
    setShifts((b.data || []).slice(0, 200));
    setSites(s.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  /** Deep-link support — `/dar?open=1&shift_id=<id>` opens the New DAR modal
   *  with the guard's just-completed shift preselected. Used by the guard-
   *  portal clock-out toast to close the shift → report gap.
   */
  useEffect(() => {
    if (open || editing) return;
    const params = new URLSearchParams(location.search);
    if (params.get("open") !== "1") return;
    const preShift = params.get("shift_id") || "";
    setEditing(null);
    setForm({ ...empty, shift_id: preShift });
    setOpen(true);
    // Consume the query so a re-render doesn't retrigger the open.
    try {
      const cleanUrl = location.pathname + (location.hash || "");
      window.history.replaceState({}, "", cleanUrl);
    } catch { /* ignore */ }
  }, [location.search, open, editing, location.pathname, location.hash]);

  /** When the "New DAR" modal opens for a guard, auto-populate `shift_id` from
   * the guard's currently-active clocked-in shift (if any). Falls back silently
   * for non-guard roles or when the guard isn't clocked in. */
  useEffect(() => {
    if (!open || editing) return; // only on fresh Create
    if (user?.role !== "guard") { setActiveShiftInfo(null); return; }
    let cancelled = false;
    api.get("/mobile/active-shift").then((r) => {
      if (cancelled) return;
      const active = r.data?.active;
      if (active?.shift?.id) {
        setActiveShiftInfo({ shift: active.shift, site: active.site });
        setForm((f) => (f.shift_id ? f : { ...f, shift_id: active.shift.id }));
        setSiteFilter((prev) => prev || active.site?.id || "");
      } else {
        setActiveShiftInfo(null);
      }
    }).catch(() => setActiveShiftInfo(null));
    return () => { cancelled = true; };
  }, [open, editing, user?.role]);

  // Sorted searchable options for the new site picker. Sites with
  // `enabled_reports.dar=false` are hidden — DAR must be enabled to appear.
  const siteOptions = useMemo(() => sites
    .filter((s) => s.enabled_reports?.dar !== false)
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || "" }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [sites]);

  /** For guard users, cull the dropdown to shifts they were clocked into
   * (any attendance record for their guard record). Cap at 5. Non-guards see
   * the full list to file DARs on behalf. `siteFilter` further narrows it. */
  const shiftOptions = useMemo(() => {
    let arr = shifts;
    if (user?.role === "guard") {
      const mine = shifts.filter((s) => {
        const ids = Array.isArray(s.guard_ids) ? s.guard_ids : (s.guard_id ? [s.guard_id] : []);
        return ids.length > 0; // server already scopes /shifts by role
      });
      arr = mine.slice(0, 5);
    }
    if (siteFilter) arr = arr.filter((s) => s.site_id === siteFilter);
    return arr;
  }, [shifts, user?.role, siteFilter]);

  // Options for the SingleSelectDropdown shift picker.
  const shiftPickerOptions = useMemo(() => shiftOptions.map((s) => ({
    value: s.id,
    label: s.site_name || "Unknown site",
    sub: fmtDateTime(s.start_time, s.site_timezone),
  })), [shiftOptions]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEquip = (k) => set("equipment_inspected", form.equipment_inspected.includes(k)
    ? form.equipment_inspected.filter((x) => x !== k)
    : [...form.equipment_inspected, k]);

  const [eventKeys, setEventKeys] = useState([]);
  const addEvent = () => {
    setEventKeys((k) => [...k, `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`]);
    set("notable_events", [...(form.notable_events || []), { at: new Date().toISOString(), text: "" }]);
  };
  const updateEvent = (i, k, v) => set("notable_events", form.notable_events.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const rmEvent = (i) => {
    setEventKeys((k) => k.filter((_, idx) => idx !== i));
    set("notable_events", form.notable_events.filter((_, idx) => idx !== i));
  };

  const onSave = async () => {
    if (!form.shift_id) { toast.error("Shift is required"); return; }
    // Officer workflow — if this guard already has a DAR on file for the
    // selected shift, offer to edit that one instead of creating a duplicate.
    if (!editing && user?.role === "guard") {
      const myGuardId = activeShiftInfo?.shift?.guard_id
        || activeShiftInfo?.shift?.guard_ids?.[0]
        || items.find((d) => d.shift_id === form.shift_id)?.guard_id;
      const existing = items.find(
        (d) => d.shift_id === form.shift_id
          && (!myGuardId || d.guard_id === myGuardId),
      );
      if (existing) {
        const filedOn = existing.created_at ? new Date(existing.created_at).toLocaleString() : "an earlier moment";
        if (window.confirm(
          `You already filed a DAR for this shift on ${filedOn}. `
          + "Edit that report instead of creating a new one?",
        )) {
          onEdit(existing);
        } else {
          // Explain the silence so the guard doesn't wonder whether their
          // click just failed — a subtle info toast beats an empty screen.
          toast.info("No changes submitted", {
            description: "Cancelled without editing your existing DAR.",
          });
        }
        return;
      }
    }
    try {
      const payload = {
        ...form,
        visitor_count: parseInt(form.visitor_count || 0, 10),
        vehicle_count: parseInt(form.vehicle_count || 0, 10),
        notable_events: (form.notable_events || []).filter((e) => e.text.trim()),
      };
      if (editing) await api.patch(`/dar/${editing.id}`, payload);
      else await api.post("/dar", payload);
      toast.success(editing ? "DAR updated" : "DAR submitted");
      const wasCreating = !editing;
      setOpen(false); setEditing(null); setForm(empty); setEventKeys([]);
      // Iter 217 — the AI summary is generated in the background (~1-3s).
      // Reload immediately + once more after a short delay so the freshly
      // stamped `ai_summary` field shows up without a manual refresh.
      load();
      setTimeout(() => load(), 3500);

      // For guards, reroute after submission — on-shift page if they're
      // still clocked in, otherwise their main (guard portal) home. Both
      // land on /guard-portal, but the underlying page conditionally
      // renders the "on shift" panel when an active attendance exists.
      if (wasCreating && user?.role === "guard") {
        try {
          const active = await api.get("/mobile/active-shift").catch(() => null);
          const stillOnShift = !!active?.data?.active?.shift?.id;
          nav("/guard-portal", { state: { fromDar: true, stillOnShift } });
        } catch {
          nav("/guard-portal");
        }
      }
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const onEdit = (d) => {
    setEditing(d);
    const events = d.notable_events || [];
    setForm({
      ...empty,
      ...d,
      equipment_inspected: d.equipment_inspected || [],
      notable_events: events,
      photo_attachments: d.photo_attachments || [],
    });
    setEventKeys(events.map((_, i) => `evt-load-${d.id || 'new'}-${i}-${Math.random().toString(36).slice(2, 7)}`));
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this DAR?")) return;
    await api.delete(`/dar/${id}`); toast.success("Removed"); load();
  };

  // Iter 217 — one-click "Remove AI summary" for the header block.
  const onClearAiSummary = async (id) => {
    try {
      await api.delete(`/dar/${id}/ai-summary`);
      toast.success("AI summary removed");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to remove AI summary"));
    }
  };

  const filtered = useMemo(() => {
    const { client_names, site_ids, guard_ids } = filters;
    if (!client_names.length && !site_ids.length && !guard_ids.length) return items;
    const cset = new Set(client_names), sset = new Set(site_ids), gset = new Set(guard_ids);
    return items.filter((d) =>
      (cset.size === 0 || cset.has(d.client_name)) &&
      (sset.size === 0 || sset.has(d.site_id)) &&
      (gset.size === 0 || (d.guard_id && gset.has(d.guard_id)))
    );
  }, [items, filters]);

  const hasFilters = !!(filters.client_names.length || filters.site_ids.length || filters.guard_ids.length);
  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";

  return (
    <div className={containerClass}>
      {!isFs && (
        <PageHeader
          title="Daily Activity Reports"
          subtitle="End-of-shift reports submitted by . Detailed narratives, counts, equipment inspections, notable events, and timestamped photos."
          action={
            <ActionButton data-testid="add-dar-button" onClick={() => { setEditing(null); setForm(empty); setEventKeys([]); setOpen(true); }} style={!canCreateDar ? { display: "none" } : undefined}>
              <Plus className="size-4" /> New DAR
            </ActionButton>
          }
        />
      )}

      <div className={isFs ? "p-6 space-y-3" : "p-4 md:p-8 space-y-3"} data-testid="dar-list">
        <StickyFiltersBar
          testIdPrefix="dar"
          value={filters}
          onChange={setFilters}
          visibleCount={filtered.length}
          totalCount={items.length}
          isFullscreen={isFs}
          fullscreenUrl={isFs ? null : "/dar/fullscreen"}
        />

        <BulkResendBar
          kind="dar"
          selected={bulk.selected}
          onClear={bulk.clear}
          testIdPrefix="dar"
        />

        {filtered.map((d) => (
          <div key={d.id} className="border border-[#27272A] bg-[#121212] p-3 md:p-5">
            <div className="flex items-start gap-2 md:gap-4">
              <label
                className="mt-1 shrink-0 inline-flex items-center cursor-pointer"
                title="Select for bulk actions"
              >
                <input
                  type="checkbox"
                  data-testid={`dar-select-${d.id}`}
                  checked={bulk.isSelected(d.id)}
                  onChange={() => bulk.toggle(d.id)}
                  className="size-4 accent-[#0A84FF]"
                />
              </label>
              <div className="size-10 shrink-0 border border-[#27272A] hidden sm:flex items-center justify-center">
                <FileSignature className="size-5 text-[#0A84FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg md:text-xl uppercase font-bold tracking-tight break-words">{d.site_name || "Unknown site"}</h3>
                  <PillBadge>{d.client_name}</PillBadge>
                  {d.guard_name && <PillBadge tone="blue">{d.guard_name}</PillBadge>}
                  {d.approval_status && <ApprovalPill status={d.approval_status} testId={`dar-approval-pill-${d.id}`} />}
                </div>
                {d.ai_summary ? (
                  <div
                    className="mt-3 p-3 border-l-2 border-l-[#8b5cf6] bg-[#1a1625]"
                    data-testid={`dar-ai-summary-${d.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-widest font-mono text-[#c4b5fd]">
                        ▲ AI Summary
                      </span>
                      <button
                        type="button"
                        onClick={() => onClearAiSummary(d.id)}
                        className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
                        data-testid={`dar-ai-summary-remove-${d.id}`}
                        title="Remove AI summary"
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <div className="text-sm text-zinc-200 whitespace-pre-line">{d.ai_summary}</div>
                  </div>
                ) : null}
                {d.dar_mode === "detailed" ? (
                  <div className="mt-3" data-testid={`dar-detailed-${d.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="label-mute">Tour Scan Log · Detailed DAR</div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {(d.tour_scan_log || []).length} scan{(d.tour_scan_log || []).length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <TourScanLogTable
                      rows={d.tour_scan_log}
                      siteTimezone={d.site_timezone}
                      testIdPrefix={`dar-scan-log-${d.id}`}
                    />
                    <details className="mt-3 text-sm text-zinc-400 group">
                      <summary
                        className="cursor-pointer select-none uppercase tracking-widest text-[10px] text-zinc-500 hover:text-zinc-300"
                        data-testid={`dar-narrative-toggle-${d.id}`}
                      >
                        <span className="group-open:hidden">Show narrative ▾</span>
                        <span className="hidden group-open:inline">Hide narrative ▴</span>
                      </summary>
                      <div className="mt-2 whitespace-pre-line">{d.narrative || <span className="italic text-zinc-500">No narrative provided.</span>}</div>
                    </details>
                  </div>
                ) : (
                  <div className="text-sm text-zinc-400 mt-2 whitespace-pre-line line-clamp-3">{d.narrative}</div>
                )}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-500 mt-3 font-mono">
                  <span>VISITORS: {d.visitor_count ?? 0}</span>
                  <span>VEHICLES: {d.vehicle_count ?? 0}</span>
                  {d.weather_conditions && <span>WEATHER: {d.weather_conditions}</span>}
                  <span>SUBMITTED: {fmtDateTime(d.created_at, d.site_timezone)}</span>
                </div>
                {(d.equipment_inspected || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {d.equipment_inspected.map((e) => <PillBadge key={e}>{e}</PillBadge>)}
                  </div>
                )}
                {(d.photo_attachments || []).length > 0 && (
                  <button
                    type="button"
                    data-testid={`view-dar-photos-${d.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0A84FF] hover:text-white"
                    onClick={() => setViewingPhotos({ items: d.photo_attachments, title: d.site_name })}
                  >
                    <ImageIcon className="size-3.5" /> {d.photo_attachments.length} photo{d.photo_attachments.length === 1 ? "" : "s"}
                  </button>
                )}
                <AdminCommentsPanel kind="dar" targetId={d.id} />
                {d.approval_status && (
                  <div className="mt-4">
                    <ApprovalActions kind="dar" report={d} onChange={() => load()} />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <GhostButton onClick={() => onEdit(d)}><Pencil className="size-3.5" /></GhostButton>
                <ResendReportEmailButton kind="dar" itemId={d.id} testId={`dar-resend-email-${d.id}`} />
                <GhostButton onClick={() => onDelete(d.id)} style={!canDeleteDar ? { display: "none" } : undefined}><Trash2 className="size-3.5" /></GhostButton>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">No DARs yet. Officers submit one at end of shift.</div>}
        {items.length > 0 && filtered.length === 0 && (
          <div className="border border-[#27272A] p-12 text-center text-zinc-500 scanlines">No DARs match the filters.</div>
        )}
      </div>

      {open && (
        <Modal title={editing ? "Edit DAR" : "New Daily Activity Report"} onClose={() => setOpen(false)} testId="dar-form-modal">
          {/* Required-fields checklist — helps guards see what's still blocking
              submit before they scroll to the bottom to hit Save. */}
          {(() => {
            const shiftOk = Boolean(form.shift_id);
            const narrOk  = form.narrative.trim().length > 0;
            const allOk   = shiftOk && narrOk;
            const dotColor = (ok) => (ok ? "#22C55E" : "#EAB308");
            return (
              <div
                data-testid="dar-required-checklist"
                className={`mb-4 p-3 border flex items-center gap-4 text-xs ${
                  allOk
                    ? "border-[#22C55E]/40 bg-[#22C55E]/5"
                    : "border-[#EAB308]/40 bg-[#EAB308]/5"
                }`}
              >
                <div className="label-mute">
                  {allOk ? "Ready to submit" : "To submit:"}
                </div>
                <div className="flex items-center gap-1.5" data-testid="dar-check-shift">
                  <span className="size-2 rounded-full" style={{ backgroundColor: dotColor(shiftOk) }} />
                  <span className={shiftOk ? "text-zinc-400 line-through" : "text-zinc-200"}>
                    Shift selected
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="dar-check-narrative">
                  <span className="size-2 rounded-full" style={{ backgroundColor: dotColor(narrOk) }} />
                  <span className={narrOk ? "text-zinc-400 line-through" : "text-zinc-200"}>
                    Narrative added
                  </span>
                </div>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><FormField label="Shift *">
              {activeShiftInfo && !editing ? (
                <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-3 text-sm" data-testid="dar-active-shift-banner">
                  <div className="text-[10px] uppercase tracking-widest text-[#34C759] mb-1">Auto-attached from current clock-in</div>
                  <div className="font-mono text-zinc-100">
                    {activeShiftInfo.site?.name || activeShiftInfo.shift?.site_name || "Site"} · {fmtDateTime(activeShiftInfo.shift.start_time, activeShiftInfo.site?.timezone || activeShiftInfo.shift?.site_timezone)}
                  </div>
                  <button
                    type="button"
                    data-testid="dar-change-shift"
                    onClick={() => { setActiveShiftInfo(null); set("shift_id", ""); }}
                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] mt-2"
                  >
                    Pick a different shift…
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {user?.role !== "guard" && (
                    <SingleSelectDropdown
                      testId="dar-site"
                      label="Filter by site (searchable)…"
                      options={siteOptions}
                      value={siteFilter}
                      onChange={(v) => {
                        setSiteFilter(v);
                        // If the currently selected shift isn't at this site, clear it.
                        if (v) {
                          const stillValid = shifts.some((s) => s.id === form.shift_id && s.site_id === v);
                          if (!stillValid) set("shift_id", "");
                        }
                      }}
                      searchable
                      triggerClassName="w-full"
                    />
                  )}
                  <SingleSelectDropdown
                    testId="dar-shift"
                    label={shiftPickerOptions.length ? "Select shift…" : (siteFilter ? "No shifts at this site" : "Select shift…")}
                    options={shiftPickerOptions}
                    value={form.shift_id}
                    onChange={(v) => set("shift_id", v)}
                    searchable
                    triggerClassName="w-full"
                  />
                </div>
              )}
              {user?.role === "guard" && !activeShiftInfo && (
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">
                  Showing your 5 most recent clocked-in shifts.
                </div>
              )}
            </FormField></div>
            <div className="col-span-2"><FormField label="Shift summary / narrative *">
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="dar-quick-phrases">
                {quickPhrases.map((phrase, i) => {
                  const slots = extractPlaceholders(phrase);
                  const hasSlots = slots.length > 0;
                  return (
                    <button
                      key={`${phrase}-${i}`}
                      type="button"
                      onClick={() => {
                        if (hasSlots) {
                          setPhraseTemplate(phrase);
                        } else {
                          set("narrative", appendPhrase(form.narrative, phrase));
                        }
                      }}
                      data-testid={`dar-quick-phrase-${i}`}
                      title={hasSlots ? `Fill in: ${slots.join(", ")}` : phrase}
                      className={`px-2 py-1 text-[10px] uppercase tracking-widest border transition-colors ${
                        hasSlots
                          ? "border-[#0A84FF]/40 text-[#0A84FF] hover:border-[#0A84FF] hover:bg-[#0A84FF]/10"
                          : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                      }`}
                    >
                      {hasSlots ? "✎ " : "+ "}Quick phrase {i + 1}
                    </button>
                  );
                })}
              </div>
              <textarea data-testid="dar-narrative" rows={4} className={inputClass} value={form.narrative} onChange={(e) => set("narrative", e.target.value)} placeholder="Overall description of the shift — what happened, who came/went, anything noteworthy." />
              <div className="text-[10px] text-zinc-500 mt-1">
                Tap a chip to append a common phrase — you can edit or combine them freely.
              </div>
            </FormField></div>
            <FormField label="Visitors logged">
              <input type="number" min="0" data-testid="dar-visitor-count" className={inputClass} value={form.visitor_count} onChange={(e) => set("visitor_count", e.target.value)} />
            </FormField>
            <FormField label="Vehicles logged">
              <input type="number" min="0" data-testid="dar-vehicle-count" className={inputClass} value={form.vehicle_count} onChange={(e) => set("vehicle_count", e.target.value)} />
            </FormField>
            <FormField label="Weather conditions">
              <input className={inputClass} placeholder='e.g. "Clear, 72°F"' value={form.weather_conditions} onChange={(e) => set("weather_conditions", e.target.value)} />
            </FormField>
            <div />
            <div className="col-span-2">
              <FormField label="Equipment inspected">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {EQUIPMENT_OPTIONS.map((opt) => (
                    <label key={opt} className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.equipment_inspected.includes(opt)} onChange={() => toggleEquip(opt)} />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </FormField>
            </div>
            <div className="col-span-2"><FormField label="Maintenance issues observed">
              <textarea rows={2} className={inputClass} value={form.maintenance_issues} onChange={(e) => set("maintenance_issues", e.target.value)} placeholder="Burnt out lights, broken locks, leaks, etc." />
            </FormField></div>

            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <FormField label="Notable events">
                <div className="space-y-2" data-testid="dar-events">
                  {(form.notable_events || []).map((e, idx) => (
                    <div key={eventKeys[idx] || `evt-idx-${idx}`} className="grid grid-cols-12 gap-2">
                      <input type="datetime-local" className={inputClass + " col-span-4"} value={e.at ? e.at.slice(0, 16) : ""} onChange={(ev) => updateEvent(idx, "at", ev.target.value ? new Date(ev.target.value).toISOString() : "")} />
                      <input className={inputClass + " col-span-7"} placeholder="What happened?" value={e.text} onChange={(ev) => updateEvent(idx, "text", ev.target.value)} />
                      <GhostButton type="button" onClick={() => rmEvent(idx)}><Trash2 className="size-3.5" /></GhostButton>
                    </div>
                  ))}
                  <GhostButton type="button" data-testid="add-event" onClick={addEvent}><Plus className="size-3.5" /> Add event</GhostButton>
                </div>
              </FormField>
            </div>

            <div className="col-span-2"><FormField label="End-of-shift handover notes">
              <textarea rows={2} className={inputClass} value={form.handover_notes} onChange={(e) => set("handover_notes", e.target.value)} placeholder="What does the next shift need to know?" />
            </FormField></div>

            <div className="col-span-2 border-t border-[#27272A] pt-4">
              <div className="label-mute mb-3">Photos</div>
              <PhotoAttachmentsField
                value={form.photo_attachments}
                onChange={(v) => set("photo_attachments", v)}
                purpose="dar"
                site={sites.find((s) => s.id === form.site_id) || null}
                guardName={user?.name || user?.email}
                testIdPrefix="dar-photo"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
            <ActionButton data-testid="save-dar-button" onClick={onSave}>Save</ActionButton>
          </div>
        </Modal>
      )}

      {viewingPhotos && (
        <Modal title={`Attachments · ${viewingPhotos.title}`} onClose={() => setViewingPhotos(null)} testId="dar-photo-viewer-modal">
          <AttachmentViewer
            items={viewingPhotos.items}
            formatTimestamp={fmtDateTime}
            testIdPrefix="dar-attachment"
          />
        </Modal>
      )}

      {phraseTemplate && (() => {
        // Auto-fill common slots from the currently selected shift + user +
        // clock. Officers can still overwrite anything — this just saves them
        // typing for the most common values.
        const selShift = shifts.find((s) => s.id === form.shift_id);
        const now = new Date();
        const initialValues = {
          site:         selShift?.site_name,
          location:     selShift?.site_name,
          time:         now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
          date:         now.toLocaleDateString([], { month: "short", day: "numeric" }),
          officer:      user?.name || user?.email,
          condition:    "normal",
        };
        return (
          <DarPhraseFillModal
            template={phraseTemplate}
            initialValues={initialValues}
            onClose={() => setPhraseTemplate(null)}
            onInsert={(resolved) => set("narrative", appendPhrase(form.narrative, resolved))}
          />
        );
      })()}
    </div>
  );
}
